import { Trans } from '@lingui/react/macro';
import AlertWarning from 'interface/AlertWarning';
import ReportIcon from 'interface/icons/Events';
import GuildIcon from 'interface/icons/People';
import CharacterIcon from 'interface/icons/Person';
import Logo from 'interface/images/logo.svg?react';
import NameSearch, { SearchType } from 'interface/NameSearch';
import { getReportHistory } from 'interface/selectors/reportHistory';
import { ReportHistoryState } from 'interface/reducers/reportHistory';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReportHistory from './ReportHistory';
import ReportSelecter from './ReportSelecter';
import { RootState } from 'store';
import './Header.scss';

enum StateSearch {
  Report,
  Character,
  Guild,
}

interface Props {
  reportHistory: ReportHistoryState;
}

interface State {
  searchType: StateSearch;
}

class ReportSelectionHeader extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      searchType: StateSearch.Report,
    };
    this.handleCharacterSearchClick = this.handleCharacterSearchClick.bind(this);
    this.handleReportSearchClick = this.handleReportSearchClick.bind(this);
    this.handleGuildSearchClick = this.handleGuildSearchClick.bind(this);
  }

  handleReportSearchClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    this.setState({
      searchType: StateSearch.Report,
    });
  }

  handleCharacterSearchClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    this.setState({
      searchType: StateSearch.Character,
    });
  }

  handleGuildSearchClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    this.setState({
      searchType: StateSearch.Guild,
    });
  }

  renderSearch() {
    switch (this.state.searchType) {
      case StateSearch.Character:
        return (
          <>
            <NameSearch type={SearchType.CHARACTER} />
            {/* oxlint-disable-next-line wowanalyzer/no-br -- Baseline suppression */}
            <br />
            <AlertWarning>
              <Trans id="interface.home.reportSelectionHeader.onlyRankedWCLogs">
                The character page will only show fights that have been ranked by Warcraft Logs.
                Wipes are not included and during busy periods there might be a delay before new
                reports appear. You can still analyze these fights by manually finding the report on
                Warcraft Logs and using the report link.
              </Trans>
            </AlertWarning>
          </>
        );
      case StateSearch.Guild:
        return <NameSearch type={SearchType.GUILD} />;
      case StateSearch.Report:
      default:
        return <ReportSelecter />;
    }
  }

  render() {
    const { reportHistory } = this.props;

    return (
      <header className="report-selection">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <a href="/" className="brand-name">
                <Logo />
                <h1>WoWAnalyzer</h1>
              </a>
              <div id="reportSelectionHeader.improveYourPerformance">
                <Trans id="interface.home.reportSelectionHeader.improveYourPerformance">
                  Improve your performance with personal feedback and stats. Just enter the link of
                  a{' '}
                  <a href="https://www.warcraftlogs.com/" target="_blank" rel="noopener noreferrer">
                    Warcraft Logs
                  </a>{' '}
                  report below.
                </Trans>
              </div>
              <div className="report-search-panel">
                <nav>
                  <ul>
                    <li
                      key="report"
                      className={
                        this.state.searchType === StateSearch.Report ? 'active' : undefined
                      }
                    >
                      <a href="/" style={{ padding: '5px' }} onClick={this.handleReportSearchClick}>
                        <ReportIcon />
                        <Trans id="interface.home.reportSelectionHeader.report">Report</Trans>
                      </a>
                    </li>
                    <li
                      key="character"
                      className={
                        this.state.searchType === StateSearch.Character ? 'active' : undefined
                      }
                    >
                      <a
                        href="/"
                        style={{ padding: '5px' }}
                        onClick={this.handleCharacterSearchClick}
                      >
                        <CharacterIcon />
                        <Trans id="interface.home.reportSelectionHeader.character">Character</Trans>
                      </a>
                    </li>
                    <li
                      key="guild"
                      className={this.state.searchType === StateSearch.Guild ? 'active' : undefined}
                    >
                      <a href="/" style={{ padding: '5px' }} onClick={this.handleGuildSearchClick}>
                        <GuildIcon />
                        <Trans id="interface.home.reportSelectionHeader.guild">Guild</Trans>
                      </a>
                    </li>
                  </ul>
                </nav>
                {this.renderSearch()}
              </div>
            </div>
            {reportHistory.length !== 0 && (
              <div className="col-md-12 text-left report-history-panel">
                <small>
                  <Trans id="interface.home.reportSelectionHeader.recentlyViewed">
                    最近查看
                  </Trans>
                </small>
                {/* oxlint-disable-next-line wowanalyzer/no-br -- Baseline suppression */}
                <br />

                <ReportHistory reportHistory={reportHistory} />
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  reportHistory: getReportHistory(state),
});

export default connect(mapStateToProps)(ReportSelectionHeader);
